import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, History } from 'lucide-react';
import {
  fetchSiteDevicesWithRatings,
  updateDeviceRatings,
  DEVICE_RATINGS_FIELDS_BY_TYPE,
  CHEMISTRY_VALUES,
  FUEL_TYPE_VALUES,
  PHASE_COUNT_VALUES,
  type DeviceWithRatings,
  type ChemistryValue,
  type FuelTypeValue,
} from '../../services/adminApi';

interface Props {
  siteId: string;
  onSelectDevice: (deviceId: string | null) => void;
  selectedDeviceId: string | null;
}

// Aggregate BESS nameplate displayed in the section header — locked design
// decision D2 from the #122 investigation: capacity_mwh on the site stays as
// "Contracted Project Size"; aggregate BESS energy is a derived display.
function computeBessAggregateMwh(devices: DeviceWithRatings[]): number | null {
  const batteries = devices.filter((d) => d.device_type === 'battery_storage');
  const total = batteries.reduce((sum, d) => sum + (d.energy_rating_kwh ?? 0), 0);
  return batteries.length === 0 || total === 0 ? null : total / 1000;
}

export function DeviceRatingsTable({ siteId, onSelectDevice, selectedDeviceId }: Props) {
  const [expanded, setExpanded] = useState<string | null>(null);

  const { data: devices, isLoading, isError } = useQuery({
    queryKey: ['site-devices-ratings', siteId],
    queryFn: () => fetchSiteDevicesWithRatings(siteId),
    enabled: !!siteId,
  });

  const bessAggregate = useMemo(
    () => (devices ? computeBessAggregateMwh(devices) : null),
    [devices]
  );

  if (isLoading) {
    return <div className="p-4 text-sm text-ems-text-muted">Loading devices...</div>;
  }
  if (isError || !devices) {
    return <div className="p-4 text-sm text-ems-red">Failed to load devices.</div>;
  }
  if (devices.length === 0) {
    return (
      <div className="bg-ems-bg-card rounded-2xl border border-ems-border p-6">
        <div className="text-sm text-ems-text-muted">
          No devices in the roster for this site yet. Devices are added by the edge via the
          roster publisher — once published, ratings can be edited here.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-ems-bg-card rounded-2xl border border-ems-border p-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-sm font-semibold text-ems-text-primary">Per-device ratings</h2>
            <p className="text-xs text-ems-text-muted mt-1 max-w-xl">
              Edit nameplate ratings when as-built hardware differs from driver defaults.
              Changes write to <code className="font-mono">config.site_devices</code> and append a
              {' '}<code className="font-mono">device_update</code> audit entry.
            </p>
          </div>
          <div className="text-xs text-right">
            <div className="text-ems-text-muted uppercase tracking-wide">
              Aggregate BESS nameplate (derived)
            </div>
            <div className="text-ems-text-primary font-mono text-sm">
              {bessAggregate === null ? '—' : `${bessAggregate.toFixed(3)} MWh`}
            </div>
          </div>
        </div>
      </div>

      <ul className="space-y-2">
        {devices.map((device) => {
          const isOpen = expanded === device.device_id;
          const isSelected = selectedDeviceId === device.device_id;
          return (
            <li
              key={device.device_id}
              className={`bg-ems-bg-card rounded-2xl border ${isSelected ? 'border-ems-green' : 'border-ems-border'}`}
            >
              <div className="flex items-center gap-3 p-3">
                <button
                  type="button"
                  onClick={() => setExpanded(isOpen ? null : device.device_id)}
                  className="flex items-center gap-2 flex-1 text-left hover:text-ems-green"
                >
                  {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  <div className="flex-1">
                    <div className="text-sm font-medium text-ems-text-primary">
                      {device.display_name}
                    </div>
                    <div className="text-xs text-ems-text-muted font-mono">
                      {device.device_id} · {device.device_type}
                      {device.block_id ? ` · ${device.block_id}` : ''}
                    </div>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() =>
                    onSelectDevice(isSelected ? null : device.device_id)
                  }
                  className={`flex items-center gap-1 text-xs px-2 py-1 rounded hover:bg-ems-border ${isSelected ? 'text-ems-green' : 'text-ems-text-muted'}`}
                  title="View change history"
                >
                  <History className="w-3.5 h-3.5" />
                  History
                </button>
              </div>
              {isOpen && (
                <DeviceRatingsRow siteId={siteId} device={device} />
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// --- Inline rating editor (per-row form) ---

function DeviceRatingsRow({
  siteId,
  device,
}: {
  siteId: string;
  device: DeviceWithRatings;
}) {
  const allowlist = DEVICE_RATINGS_FIELDS_BY_TYPE[device.device_type] ?? [];
  const [values, setValues] = useState<Record<string, string>>(() => initValues(device, allowlist));
  const [error, setError] = useState('');
  const [successNote, setSuccessNote] = useState('');
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async () => {
      const payload = buildPayload(values, allowlist);
      return updateDeviceRatings(siteId, device.device_id, payload);
    },
    onSuccess: (res) => {
      setError('');
      setSuccessNote(
        res.changed.length
          ? `Saved — ${res.changed.length} field${res.changed.length === 1 ? '' : 's'} changed`
          : 'No changes to save'
      );
      queryClient.invalidateQueries({ queryKey: ['site-devices-ratings', siteId] });
      queryClient.invalidateQueries({ queryKey: ['device-audit-log', siteId, device.device_id] });
    },
    onError: (err: { response?: { data?: { error?: string } }; message?: string }) => {
      setSuccessNote('');
      setError(err.response?.data?.error || err.message || 'Failed to save');
    },
  });

  if (allowlist.length === 0) {
    return (
      <div className="px-4 pb-3 text-xs text-ems-text-muted border-t border-ems-border pt-3">
        No editable rating fields for <code className="font-mono">{device.device_type}</code>.
      </div>
    );
  }

  // Generator drift warning — site capacity aggregation reads max_power_kw
  // (see workspace CLAUDE.md > Capacity Metric Definitions), device detail
  // reads power_rating_kw. Both columns are editable during the deprecation
  // window; flag divergence so admins don't silently create stale aggregates.
  const showGeneratorDrift =
    device.device_type === 'generator' &&
    device.power_rating_kw !== null &&
    device.max_power_kw !== null &&
    device.power_rating_kw !== device.max_power_kw;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        mutation.mutate();
      }}
      className="border-t border-ems-border p-4 space-y-3"
    >
      {showGeneratorDrift && (
        <div className="text-xs text-amber-200 border border-amber-400/30 bg-amber-500/10 rounded p-2">
          ⚠ <code className="font-mono">power_rating_kw</code> ({device.power_rating_kw}) and
          {' '}<code className="font-mono">max_power_kw</code> ({device.max_power_kw}) differ.
          Site capacity aggregation reads <code className="font-mono">max_power_kw</code>;
          device detail reads <code className="font-mono">power_rating_kw</code>. Keep both in
          sync until <code className="font-mono">max_power_kw</code> is retired.
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {allowlist.map((field) => (
          <RatingField
            key={field}
            field={field}
            value={values[field] ?? ''}
            onChange={(v) => setValues((prev) => ({ ...prev, [field]: v }))}
          />
        ))}
      </div>
      {error && <div className="text-xs text-ems-red">{error}</div>}
      {successNote && <div className="text-xs text-ems-green">{successNote}</div>}
      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={() => {
            setValues(initValues(device, allowlist));
            setError('');
            setSuccessNote('');
          }}
          className="text-xs px-3 py-1.5 rounded border border-ems-border hover:bg-ems-border"
        >
          Reset
        </button>
        <button
          type="submit"
          disabled={mutation.isPending}
          className="text-xs px-3 py-1.5 rounded bg-ems-green text-black font-medium disabled:opacity-50"
        >
          {mutation.isPending ? 'Saving...' : 'Save ratings'}
        </button>
      </div>
    </form>
  );
}

function RatingField({
  field,
  value,
  onChange,
}: {
  field: keyof DeviceWithRatings;
  value: string;
  onChange: (v: string) => void;
}) {
  const label = FIELD_LABELS[field] ?? String(field);

  if (field === 'chemistry') {
    return (
      <label className="flex flex-col gap-1">
        <span className="text-xs text-ems-text-muted">{label}</span>
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="text-sm bg-ems-bg border border-ems-border rounded px-2 py-1.5"
        >
          <option value="">—</option>
          {CHEMISTRY_VALUES.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </label>
    );
  }
  if (field === 'fuel_type') {
    return (
      <label className="flex flex-col gap-1">
        <span className="text-xs text-ems-text-muted">{label}</span>
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="text-sm bg-ems-bg border border-ems-border rounded px-2 py-1.5"
        >
          <option value="">—</option>
          {FUEL_TYPE_VALUES.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </label>
    );
  }
  if (field === 'phase_count') {
    return (
      <label className="flex flex-col gap-1">
        <span className="text-xs text-ems-text-muted">{label}</span>
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="text-sm bg-ems-bg border border-ems-border rounded px-2 py-1.5"
        >
          <option value="">—</option>
          {PHASE_COUNT_VALUES.map((c) => (
            <option key={c} value={String(c)}>
              {c}
            </option>
          ))}
        </select>
      </label>
    );
  }

  // Default: numeric text input
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs text-ems-text-muted">{label}</span>
      <input
        type="number"
        inputMode="decimal"
        step="any"
        min="0"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="text-sm bg-ems-bg border border-ems-border rounded px-2 py-1.5 font-mono"
        placeholder="—"
      />
    </label>
  );
}

const FIELD_LABELS: Partial<Record<keyof DeviceWithRatings, string>> = {
  power_rating_kw: 'Power rating (kW)',
  energy_rating_kwh: 'Energy rating (kWh)',
  chemistry: 'Chemistry',
  max_power_kw: 'Max power (kW)',
  fuel_type: 'Fuel type',
  phase_count: 'Phase count',
  dc_rating_kw: 'DC rating (kW)',
  ac_rating_kw: 'AC rating (kW)',
  ct_ratio_primary: 'CT ratio primary',
  ct_ratio_secondary: 'CT ratio secondary',
  voltage_class_v: 'Voltage class (V)',
  port_count: 'Port count',
};

function initValues(
  device: DeviceWithRatings,
  allowlist: readonly (keyof DeviceWithRatings)[]
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const field of allowlist) {
    const raw = device[field];
    out[field] = raw === null || raw === undefined ? '' : String(raw);
  }
  return out;
}

function buildPayload(
  values: Record<string, string>,
  allowlist: readonly (keyof DeviceWithRatings)[]
): Record<string, number | string | null> {
  const out: Record<string, number | string | null> = {};
  for (const field of allowlist) {
    const raw = values[field] ?? '';
    if (raw === '') {
      out[field as string] = null;
      continue;
    }
    if (field === 'chemistry') {
      out[field] = raw as ChemistryValue;
    } else if (field === 'fuel_type') {
      out[field] = raw as FuelTypeValue;
    } else {
      const n = Number(raw);
      out[field as string] = Number.isFinite(n) ? n : null;
    }
  }
  return out;
}
